import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Input,
  Radio,
  Stack,
  Text,
} from '@chakra-ui/react'
import { RadioGroupFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import { FormLabel } from '@/components'
import { ContractReplacement } from '@/components/ContractReplacement'
import { InputGroup } from '@/components/InputGroup'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup } from '@/containers/forms/RadioGroup'
import { PoleasInformationForm } from '@/containers/steps/poleas/SignIn/PoleasInformationForm'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { Apps } from '@/dtos'
import { useSelections } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import { formatDate } from '@/utils/formatDate'

const suggestDates = (args?: { count: number }) => {
  const { count } = args ?? { count: 5 }
  const today = new Date()

  return Array.from({ length: count }).map((_, index) => {
    const date = new Date(today)
    date.setDate(date.getDate() + index + 1)
    return date
  })
}

export const InformationForm = () => {
  const { app } = useApp()
  const isPublicPoleas = app === Apps.publicPoleas
  const nameof = useNameof<InsuranceParamsDto>()

  const methods = useFormContext<InsuranceParamsDto>()
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
    watch
  } = methods

  const { isDisabled } = useInsuranceParams()
  const isJapPoleasing = !!getValues('importedData')

  const suggestedDates = suggestDates()

  const handleSuggestDateClick = (date: Date) => {
    if (date)
      setValue('generalParams.insuranceStartDate', date)
  }

  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)

  const paymentType = watch('generalParams.paymentType')
  const { data: dataPt } = useSelections(SelectionType.ZpusobPlatby)

  const pastInsuranceEnd = watch('generalParams.pastInsuranceEnd')
  return (
    <>
      <FormBody>
        <FormRow
          sx={{
            gap: {
              base: 0,
              lg: 8
            }
          }}
        >
          <FormControl isRequired isInvalid={!!errors.generalParams?.insuranceStartDate}>
            <FormLabel>Datum počátku pojištění</FormLabel>

            <Datepicker
              isDisabled={isDisabled || isPublicPoleas}
              name='generalParams.insuranceStartDate'
            />

            {isPublicPoleas && pastInsuranceEnd &&
              <FormHelperText>
                <Flex>
                  <Text fontWeight='bold'>
                    {formatDate(new Date(pastInsuranceEnd))}
                  </Text>
                  <Text>
                    &nbsp;- poslední den Vašeho pojištění (včetně).
                  </Text>
                </Flex>
              </FormHelperText>
            }
          </FormControl>

          {!isPublicPoleas &&
            <FormControl>
              <FormLabel
                sx={{
                  display: {
                    base: 'none',
                    lg: 'block',
                  },
                }}
              >
                &nbsp;
              </FormLabel>

              {!isDisabled &&
                <Stack direction="row" spacing={2}>
                  {suggestedDates.map((date) => (
                    <Button
                      key={date.toISOString()}
                      variant="ghost"
                      size="sm"
                      sx={{ h: 10 }}
                      onClick={() => handleSuggestDateClick(date)}
                    >
                      {formatDate(date, 'DD.MM.')}
                    </Button>
                  ))}
                </Stack>
              }
            </FormControl>
          }

          {errors.generalParams?.insuranceStartDate && <FormErrorMessage>{errors.generalParams.insuranceStartDate.message}</FormErrorMessage>}
        </FormRow>

        {app != Apps.extra &&
          <>
            {getValues('generalParams.contractReplacementNumber') && <FormRow itemsCount={1}>
              <ContractReplacement isDisabled defaultReplacement={!!getValues('generalParams.contractReplacementNumber')} />
            </FormRow>}

            <FormRow>

              <FormControl>
                <FormLabel>Frekvence platby</FormLabel>

                <FormHelperText>
                  Frekvence platby menší než 1x za rok může zvýšit celkové
                  pojistné
                </FormHelperText>

                <RadioGroup
                  name={nameof(x => x.generalParams.paymentFrequency)}
                  isDisabled={isDisabled}
                  stackProps={{
                    direction: 'column',
                    spacing: 4,
                  }}
                >
                  {dataPf?.data.filter((item) => item.id != Selection.OneTime).map((item) => (
                    <Radio value={item.id.toString()} key={item.id.toString()}>
                      {item.description}
                    </Radio>
                  ))}
                </RadioGroup>
              </FormControl>

              {((dataPt?.data.length ?? 0) > 1 || !dataPf?.data.some(x => x.id == paymentType)) && !isPublicPoleas &&
                <RadioGroupFormControl name={nameof(x => x.generalParams.paymentType)}
                  valueAsNumber
                  isDisabled={isDisabled}
                  label="Způsob platby"
                  helperText="&nbsp;"
                  helperTextPosition='top'
                >
                  <Stack gap={3}>
                    {dataPt?.data.map((item) => (
                      <Radio value={item.id.toString()} key={item.id.toString()}>
                        {item.description}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroupFormControl>
              }
            </FormRow>
          </>
        }

        {!isPublicPoleas &&
          <FormControl sx={{ w: '100%' }}>
            <FormLabel>Název nabídky</FormLabel>

            <Input
              placeholder="Napište název nabídky"
              isDisabled={isDisabled}
              {...register('generalParams.name')}
            />
          </FormControl>
        }

        {isJapPoleasing && !isPublicPoleas &&
          <FormRow>
            <FormControl>
              <FormLabel>Kontaktní email</FormLabel>

              <Input
                isDisabled={isDisabled}
                {...register('generalParams.contactEmail')}
              />
            </FormControl>

            <FormControl isInvalid={!!errors.generalParams?.previousLeasingNumber}>
              <FormLabel>Číslo leasingové smlouvy</FormLabel>

              <InputGroup
                isDisabled={isDisabled}
                {...register('generalParams.previousLeasingNumber')}
                errors={errors.generalParams?.previousLeasingNumber}
              />
            </FormControl>
          </FormRow>
        }

        {isPublicPoleas && <PoleasInformationForm />}

      </FormBody>
    </>
  )
}